import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, UserCheck, UserPlus, Users } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useCheckFollowQuery,
  useFollowUserMutation,
  useRequestFriendshipMutation,
  useUnfollowUserMutation,
} from '@/services/socialApi';
import { cn } from '@/lib/cn';

interface PlayerSocialButtonsProps {
  /**
   * The target user's QLASH id as understood by QSocials — i.e. their Logto
   * `sub`.
   *
   * ⚠️ PROVA / KNOWN-LIMITED: tournament participants currently only expose
   * the Laravel numeric user id (`profile.user.id`), NOT the Logto sub that
   * QSocials keys on. Callers pass that numeric id as a stand-in so the
   * buttons render and the follow/friend flow is exercisable end-to-end, but
   * the requests will not resolve to a real user until the backend
   * `Laravel user id → Logto sub` bridge ships. See
   * docs/HANDOFF-cr-tournaments-social.md §3.
   */
  userId: string;
  /**
   * When false, the buttons render (layout prova) but are inert: no QSocials
   * reads or writes fire. Used while `userId` is still a Laravel numeric id
   * stand-in — firing would create junk QSocials rows against PROD (the API
   * does NOT validate targets). Flip to true once a real Logto sub is wired
   * in (Phase 2 of the identity bridge). Defaults to true.
   */
  interactive?: boolean;
  className?: string;
}

/**
 * Compact "Follow" + "Add friend" action pair for any rendered player. Mirrors
 * the QSocials mutations used by <FriendListItem> but in a minimal,
 * mode-less form suitable for embedding in a participant/opponent row.
 */
export function PlayerSocialButtons({
  userId,
  interactive = true,
  className,
}: PlayerSocialButtonsProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();

  const followCheck = useCheckFollowQuery(userId, { skip: !userId || !interactive });
  const iFollow = followCheck.data?.iFollow ?? false;

  const [followUser, following] = useFollowUserMutation();
  const [unfollowUser, unfollowing] = useUnfollowUserMutation();
  const [requestFriendship, requesting] = useRequestFriendshipMutation();
  const [requestSent, setRequestSent] = useState(false);

  const followBusy = following.isLoading || unfollowing.isLoading;

  const onToggleFollow = () => {
    if (!interactive) return;
    haptics.impact('light');
    if (iFollow) unfollowUser({ followedUserId: userId });
    else followUser({ followedUserId: userId });
  };

  const onAddFriend = async () => {
    if (!interactive || requestSent || requesting.isLoading) return;
    haptics.impact('light');
    try {
      await requestFriendship({ friendUserId: userId }).unwrap();
      haptics.notification('success');
      setRequestSent(true);
    } catch {
      haptics.notification('error');
    }
  };

  return (
    <div className={cn('flex shrink-0 items-center gap-1.5', className)}>
      <Pill
        active={iFollow}
        disabled={!interactive || followBusy}
        onClick={onToggleFollow}
        icon={
          followBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : iFollow ? (
            <UserCheck className="h-3.5 w-3.5" />
          ) : (
            <UserPlus className="h-3.5 w-3.5" />
          )
        }
        label={iFollow ? t('social.following') : t('social.follow')}
      />
      <Pill
        active={requestSent}
        disabled={!interactive || requestSent || requesting.isLoading}
        onClick={onAddFriend}
        icon={
          requesting.isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : requestSent ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Users className="h-3.5 w-3.5" />
          )
        }
        label={requestSent ? t('social.request_sent') : t('social.add_friend')}
      />
    </div>
  );
}

interface PillProps {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function Pill({ active, disabled, onClick, icon, label }: PillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-opacity active:opacity-80 disabled:opacity-50',
        active ? 'bg-brand/15 text-brand' : 'bg-bg-tertiary text-fg-muted',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
